/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { flattenArray } from '../../common/utils/common.util';
import {
  CA_REPORT,
  CostComparison,
  CostPerUnit,
  ReportLineItem,
  ReportGeneralInfo,
  ReportLineItemTypes,
  CreateLineItemDTO,
  ReportStatusEnum,
} from '../report/report.type';
import { ProjectDraw } from '../project/project.type';
import { ROUTES } from '../../common/constants/urls';
import { UNDEFINED_CHANGE_ORDER_DIVISION_CODE } from '../../common/constants/general';
import { Division, PredictedLineItemWithDivision } from '../glossary/glossary.type';
import LineItemEditDialog from './LineItemEditDialog';
import ProjectDrawSelectionDialog from './ProjectDrawSelectionDialog';
import { calculateCdccCompTotal, isReportCompleted } from './report.util';
import { deleteReport } from '../deleteItem/deleteItem.api';
import { handleError } from '../../common/utils/util.errors';
import { usePrompt } from '../AppPrompt';
import { useQueryClient } from 'react-query';
import keys from '../../common/react-query/data-keys';
import { type MRT_ColumnDef } from 'material-react-table';
import { getTotalsRow } from './report.util';
import {
  deleteReportComp,
  fetchDivisionProjects,
  generateCompProjectAnalysis,
  updateDivisionProjectDraws,
  copyReport,
  addAllCompsPricing,
  createLineItem,
  updateDivisionComps,
  getCompsDivisionAverages,
} from './report.api';
import { getReportColumns } from './ReportColumns';
import { toast } from 'react-toastify';
import { TOTALS_DIVISIONS, TOTAL_ID } from './constants';
import { useAppContext } from 'common/AppContext';
import WhiteFab from '../common/WhiteFab';
import LineItemNoteDialog from './LineItemNoteDialog';
import { createCostAnalysisReportPdf } from 'common/pdfmake/CostAnalysisReport';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { NoteDTO } from 'common/utils/util.type';
import AutoAssignDivisionsDialog from './AutoAssignDivisionsDialog';
import useAutoDivisionMapper from '../glossary/useAutoDivisionMapper';
import { setupFontsForPdfMake } from 'common/pdfmake/pdfmake.util';
import GenericCopyReportDialog from '../general/GenericCopyReportDialog';
import { useAuth } from '../../contexts/AuthContext';
import ReportRenderer from './ReportRenderer';
import DivisionSelectionDialog from './DivisionSelectionDialog';
import AddCompsWithDivisionsDialog from './AddCompsWithDivisionsDialog';

interface ReportProps {
  report: CA_REPORT;
  updateReport: (updateFn: (prevReport: CA_REPORT) => CA_REPORT) => void;
  isReportDirty: boolean;
  isReportLoading: boolean;
  divisions: Division[];
  saveReport: (optionalReportFields?: Partial<CA_REPORT>) => void;
  undoChanges: () => void;
  refetchReport: () => void;
  isSaving: boolean;
}
interface SelectedProjectDraws {
  [divisionId: string]: string[];
}

export type ReturnCompPricingFunction = () => Promise<CreateLineItemDTO>;

export const Report: React.FC<ReportProps> = ({
  report,
  updateReport,
  isReportDirty,
  isReportLoading,
  divisions,
  saveReport,
  undoChanges,
  refetchReport,
  isSaving,
}) => {
  const [selectedLineItem, setSelectedLineItem] = useState<ReportLineItem | null>(null);
  const [notes, setNotes] = useState<NoteDTO[]>([]);
  const [lineItemEditDialogOpen, setLineItemEditDialogOpen] = useState(false);
  const [lineItemNoteDialogOpen, setLineItemNoteDialogOpen] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  const [isAddingComps, setIsAddingComps] = useState(false);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [openDialogDivisionId, setOpenDialogDivisionId] = useState<string | null>(null);
  const [openDialogDivisionName, setOpenDialogDivisionName] = useState<string | null>(null);
  const [openDialogDivisionCode, setOpenDialogDivisionCode] = useState<string | null>(null);
  const [isDeletingComp, setIsDeletingComp] = useState(false);
  const [deletingCompId, setDeletingCompId] = useState(null);
  const [openCopyReportDialog, setOpenCopyReportDialog] = useState(false);
  const { user } = useAuth();
  const isReportAuthorSameAsLoggedInUser = user?.id === report?.createdBy?.id;
  const [isLocked, setIsLocked] = useState(!isReportAuthorSameAsLoggedInUser);
  const [compLoading, setCompLoading] = useState(false);
  const { openAutoAssignDialog, isAutoAssignDialogOpen, lineItemsToAutoAssign, predictingDivisions, setIsAutoAssignDialogOpen } =
    useAutoDivisionMapper(divisions);
  const returnCompRefs: React.MutableRefObject<{ [key: string]: ReturnCompPricingFunction | undefined }> = React.useRef({});
  const [selectedProjectDraws, setSelectedProjectDraws] = useState<SelectedProjectDraws>({});
  const navigate = useNavigate();
  const { getConfirmation } = usePrompt();
  const queryClient = useQueryClient();
  const { setPageHeaderText } = useAppContext();
  const divisionTablesRef = React.useRef(new Map<string, any[]>());
  const isReportDirtyRef = React.useRef(isReportDirty);
  isReportDirtyRef.current = isReportDirty;
  const [isDivisionSelectionDialogOpen, setIsDivisionSelectionDialogOpen] = useState(false);
  const [isApplyingToDivisions, setIsApplyingToDivisions] = useState(false);
  // Add state for new stepper dialog
  const [isAddCompsWithDivisionsDialogOpen, setIsAddCompsWithDivisionsDialogOpen] = useState(false);
  const [selectedComparableIds, setSelectedComparableIds] = useState<string[]>([]);
  const [shouldRunPricing, setShouldRunPricing] = useState(false);

  const handleCloseDivisionSelectionDialog = React.useCallback(() => {
    setIsDivisionSelectionDialogOpen(false);
    setIsApplyingToDivisions(false);
    setSelectedComparableIds([]); // Clear any selected comparable IDs
  }, []);

  const handleOpenDivisionSelectionForComps = React.useCallback(() => {
    setIsDivisionSelectionDialogOpen(true);
  }, []);

  // Get selected comparable details for display in chips
  const selectedComparables = React.useMemo(() => {
    return report.selectedComps
      .filter(comp => selectedComparableIds.includes(comp.id))
      .map(comp => ({
        id: comp.id,
        title: comp.project.projectTitle || 'Unknown Title',
        number: comp.project.projectNumber || 'Unknown Number',
      }));
  }, [report.selectedComps, selectedComparableIds]);

  const handleEditDivisionsForComps = React.useCallback(
    async (selectedDivisionIds: string[]) => {
      setIsApplyingToDivisions(true);
      try {
        // Use selectedComparableIds directly (these are compAnalysisIds)
        let compAnalysisIdsToAffect: string[] = [];
        if (selectedComparableIds.length > 0) {
          compAnalysisIdsToAffect = selectedComparableIds;
        }

        await Promise.all(
          divisions.map(async division => {
            const res = await getCompsDivisionAverages(report.id, division.id);
            const currentIncluded = res.filter(comp => comp.isIncluded).map(comp => comp.compId);
            let newIncluded: string[];
            if (selectedDivisionIds.includes(division.id)) {
              // Add only the selected comps to included list (avoid duplicates)
              newIncluded = Array.from(new Set([...currentIncluded, ...compAnalysisIdsToAffect]));
            } else {
              // Remove only the selected comps from included list
              newIncluded = currentIncluded.filter(id => !compAnalysisIdsToAffect.includes(id));
            }
            await updateDivisionComps(report.id, {
              divisionId: division.id,
              newProjectDrawIds: newIncluded,
            });
            queryClient.invalidateQueries([keys.compsAverages, report.id, division.id]);
          }),
        );
        await refetchReport();
        handleCloseDivisionSelectionDialog();
        setSelectedComparableIds([]); // Clear selection after applying
        setShouldRunPricing(true); // Trigger pricing after next report update
      } catch (error) {
        handleError(error);
      } finally {
        setIsApplyingToDivisions(false);
      }
    },
    [divisions, report.id, selectedComparableIds, handleCloseDivisionSelectionDialog, refetchReport, queryClient, report.selectedComps],
  );

  useEffect(() => {
    if (report?.generalInfo?.reportName) setPageHeaderText(report?.generalInfo?.reportName);
    return () => {
      setPageHeaderText('');
    };
  }, [report?.generalInfo?.reportName]);

  const updateDivisions = React.useCallback(
    (updatedLineItems: PredictedLineItemWithDivision[]) => {
      const updatedReportLineItems = report.lineItems.map(li => {
        const updatedLineItem = updatedLineItems.find(uli => uli.id === li.id);
        if (updatedLineItem) {
          return { ...li, division: updatedLineItem.division, updateDivisionGlobally: true, isEdited: true };
        }
        return li;
      });
      updateReport(report => ({ ...report, lineItems: updatedReportLineItems }));
    },
    [report.lineItems, updateReport],
  );

  React.useEffect(() => {
    if (!isReportDirty && isReportCompleted(report)) {
      setIsLocked(true);
    }
  }, [isReportDirty, report?.status]);

  // Updated registerReturnComp function with types for parameters
  const registerReturnComp = React.useCallback(
    (divisionId: string, returnCompFunc: ReturnCompPricingFunction | undefined) => {
      if (returnCompFunc) {
        returnCompRefs.current[divisionId] = returnCompFunc;
      } else {
        delete returnCompRefs.current[divisionId];
      }
    },
    [returnCompRefs.current],
  );

  const handleGlobalAddComps = React.useCallback(async () => {
    const results = await Promise.all(
      Object.values(returnCompRefs.current).map(returnCompFunc => (returnCompFunc ? returnCompFunc() : Promise.resolve(null))),
    );
    try {
      const reportId = report.id;
      if (isReportDirty) {
        const isConfirmed = await getConfirmation({
          description: 'This will overwrite any unsaved changes in the Report. Are you sure you want to continue?',
          confirmButton: {
            label: 'Continue',
            color: 'error',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });
        if (!isConfirmed) return;
      }

      // Filter out null values and items with cdccCompPerUnit equal to 0
      const validResults: CreateLineItemDTO[] = results.filter((item): item is CreateLineItemDTO => item !== null);

      if (validResults.length > 0) {
        await addAllCompsPricing(reportId, validResults);
        toast.success('Comparable Pricing added successfully!');
      } else {
        toast.error('No valid Comparable Pricing to add');
      }
    } catch (error) {
      handleError(error);
    }
    refetchReport();
  }, [report.id, returnCompRefs.current, isReportDirty, report.selectedComps]);

  // Opens the new AddCompsWithDivisionsDialog (new flow)
  const handleOpenCompsDialog = React.useCallback(() => {
    setIsAddCompsWithDivisionsDialogOpen(true);
  }, []);

  const onDeleteComp = React.useCallback(
    async (compId: any) => {
      setDeletingCompId(compId);
      setIsDeletingComp(true);
      setCompLoading(true);
      try {
        const isConfirmed = await getConfirmation({
          description: isReportDirty
            ? 'Deleting this comp will overwrite any unsaved changes in the Report. Are you sure you want to continue?'
            : 'Are you sure you want to delete this comp?',
          confirmButton: {
            label: 'Delete',
            color: 'error',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });
        if (!isConfirmed) return;
        await deleteReportComp(compId);
        await queryClient.invalidateQueries([keys.compsAverages]);
        await queryClient.invalidateQueries(keys.divisions);
        refetchReport();
        toast.success('Comp deleted successfully.');
        await handleGlobalAddComps();
      } catch (error) {
        handleError(error);
      } finally {
        setIsDeletingComp(false);
        setDeletingCompId(null);
        setCompLoading(false);
      }
    },
    [isReportDirty, report.id, handleGlobalAddComps],
  );

  const handleProjectDrawSelectionChange = React.useCallback((divisionId: string, newSelectedProjectDraws: string[]) => {
    setSelectedProjectDraws(selectedProjectDraws => ({ ...selectedProjectDraws, [divisionId]: newSelectedProjectDraws }));
  }, []);

  const handleOpenProjectDialog = React.useCallback((divisionId: string, divisionCode: string, divisionName: string) => {
    setIsProjectDialogOpen(true);
    setOpenDialogDivisionId(divisionId);
    setOpenDialogDivisionName(divisionName);
    setOpenDialogDivisionCode(divisionCode);
  }, []);

  //TODO: double check what this does
  const handleCloseDialog = React.useCallback(() => {
    setOpenDialogDivisionId(null);
    setOpenDialogDivisionName(null);
    setIsProjectDialogOpen(false);
    setLineItemNoteDialogOpen(false);
    setSelectedLineItem(null);
    setOpenCopyReportDialog(false);
  }, []);

  useEffect(() => {
    fetchDivisionProjects(report.id)
      .then((response: any) => {
        // Assuming that the response directly contains the data
        const initialSelectedProjectDraws: SelectedProjectDraws = {};

        for (const divisionId in response) {
          initialSelectedProjectDraws[divisionId] = response[divisionId].map((project: ProjectDraw) => project.id);
        }

        setSelectedProjectDraws(initialSelectedProjectDraws);
      })
      .catch(error => {
        console.error('Error fetching division projects:', error);
        // Handle the error scenario appropriately
      });
  }, [report.id]);

  const handleupdatenote = React.useCallback(
    (noteContent: string) => {
      setIsSavingNote(true);
      const updatedLineItems = report.lineItems.map(lineItem => {
        if (lineItem.id === selectedLineItem?.id) {
          const updatedNote: NoteDTO = {
            content: noteContent,
          };
          return { ...selectedLineItem, note: updatedNote, isEdited: true };
        }
        return lineItem;
      });
      updateReport(report => ({ ...report, lineItems: updatedLineItems }));
      setIsSavingNote(false);
      handleCloseDialog();
    },
    [report],
  );

  // Arrange line items into division tables
  const divisionTables: { [key: string]: any[] } = React.useMemo(() => {
    const lineItems = report?.lineItems?.filter(li => !li.isDeleted) || [];
    const newDivisionData = new Map<string, any[]>();

    divisions.forEach(division => {
      const currDivisionItems = lineItems.filter(item => item.division?.id === division.id);

      // Reuse old reference if data is the same
      const prevDivisionItems = divisionTablesRef.current.get(division.id);
      const areSomeLineItemsModified = currDivisionItems.some(li => li.isEdited);
      if (isReportDirty && !areSomeLineItemsModified && prevDivisionItems?.length === currDivisionItems?.length) {
        newDivisionData.set(division.id, prevDivisionItems || currDivisionItems);
      } else {
        newDivisionData.set(
          division.id,
          currDivisionItems.map(item => ({
            ...item,
            divisionName: division.name,
            isDivisionHeader: false,
          })),
        );
      }
    });

    // Handle "No Division" items separately — also include division 21 (Undefined/vague Change Orders)
    const currNoDivisionItems = lineItems.filter(item => !item.division || item.division.code === UNDEFINED_CHANGE_ORDER_DIVISION_CODE);
    const prevNoDivisionItems = divisionTablesRef.current.get('no-division');

    const areSomeLineItemsModified = currNoDivisionItems.some(li => li.isEdited || li.isDeleted);
    if (isReportDirty && !areSomeLineItemsModified && prevNoDivisionItems?.length === currNoDivisionItems?.length) {
      newDivisionData.set('no-division', prevNoDivisionItems || currNoDivisionItems);
    } else {
      newDivisionData.set(
        'no-division',
        currNoDivisionItems.map(item => ({
          ...item,
          divisionName: 'No Division',
          isDivisionHeader: false,
        })),
      );
    }

    // Update ref with latest values
    divisionTablesRef.current = newDivisionData;
    return Object.fromEntries(newDivisionData); // Convert Map to normal object
  }, [divisions, report.lineItems, isReportDirty]);

  const onDeleteReport = React.useCallback(async () => {
    setIsDeletingReport(true);
    try {
      const isConfirmed = await getConfirmation({
        description: 'Are you sure you want to delete this report?',
        confirmButton: {
          label: 'Delete',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;
      await deleteReport(report.id);
      queryClient.invalidateQueries(keys.allReports);
      toast.success('Report deleted successfully.');
      navigate(ROUTES.CA_REPORTS);
    } catch (error) {
      toast.error('Error deleting report.');
      handleError(error);
    } finally {
      setIsDeletingReport(false);
    }
  }, [report.id]);

  const sortedDivisions = React.useMemo(() => {
    return [...divisions].sort((a, b) => Number(a.code) - Number(b.code));
  }, [divisions]);

  // Keep references stable, avoid unnecessary re-renders
  const sortedTables = React.useMemo(() => {
    return sortedDivisions.map(division => divisionTables[division.id] || []);
  }, [divisionTables, sortedDivisions]);

  const hardCostSubTotalRow = React.useMemo(() => {
    const totalLineItem = report.lineItems.filter(li => li.type == ReportLineItemTypes.HardCostSubTotal)[0] || undefined;
    return [
      getTotalsRow(
        'HARD COST SUBTOTAL',
        flattenArray(sortedTables, TOTALS_DIVISIONS.HARD_COSTS_START_INDEX, TOTALS_DIVISIONS.HARD_COSTS_END_INDEX).filter(
          (li: any) => li.type != ReportLineItemTypes.DivisionTotal,
        ),
        report.generalInfo?.grossBuildingSF ?? 0,
        'no-division',
        undefined,
        undefined,
        totalLineItem,
      ),
    ];
  }, [sortedTables, report]);

  const projectedHardCostSubTotalRow = React.useMemo(() => {
    const totalLineItem = report.lineItems.filter(li => li.type == ReportLineItemTypes.TotalProjectedHardCosts)[0] || undefined;
    const contractor_overhead_and_contingency = [
      ...sortedTables[TOTALS_DIVISIONS.CONTRACTOR_OVERHEAD_INDEX],
      ...sortedTables[TOTALS_DIVISIONS.CONTRACTOR_CONTINGENCY_INDEX],
    ].filter((li: any) => li.type != ReportLineItemTypes.DivisionTotal);
    const projectedTotalHardCost = [...contractor_overhead_and_contingency, ...hardCostSubTotalRow];
    return [
      getTotalsRow(
        'TOTAL PROJECTED HARD COSTS',
        projectedTotalHardCost,
        report.generalInfo?.grossBuildingSF ?? 0,
        'no-division',
        undefined,
        undefined,
        totalLineItem,
      ),
    ];
  }, [sortedTables, report]);

  const softCostSubTotalRow = React.useMemo(() => {
    const totalLineItem = report.lineItems.filter(li => li.type == ReportLineItemTypes.TotalSoftCosts)[0] || undefined;
    return [
      getTotalsRow(
        'TOTAL SOFT COSTS',
        [...sortedTables[TOTALS_DIVISIONS.SOFT_COSTS_START_INDEX], ...sortedTables[TOTALS_DIVISIONS.SOFT_COSTS_END_INDEX]].filter(
          (li: any) => li.type != ReportLineItemTypes.DivisionTotal,
        ),
        report.generalInfo?.grossBuildingSF ?? 0,
        'no-division',
        undefined,
        undefined,
        totalLineItem,
      ),
    ];
  }, [sortedTables, report]);

  const totalProjectCostsRow = React.useMemo(() => {
    const totalLineItem = report.lineItems.filter(li => li.type == ReportLineItemTypes.TotalProjectedCosts)[0] || undefined;
    return [
      getTotalsRow(
        'TOTAL PROJECTED COSTS',
        [...projectedHardCostSubTotalRow, ...softCostSubTotalRow],
        report.generalInfo?.grossBuildingSF ?? 0,
        'no-division',
        undefined,
        undefined,
        totalLineItem,
      ),
    ];
  }, [projectedHardCostSubTotalRow, softCostSubTotalRow, report]);

  const totalUnassignedCostsRow = React.useMemo(() => {
    return [
      getTotalsRow(
        'TOTAL',
        divisionTables['no-division']?.filter(row => row.type == null || row.type === 'Normal') || [],
        report.generalInfo.grossBuildingSF || 1,
        'no-division',
        undefined,
        undefined,
        divisionTables['no-division']?.filter(row => row.type === ReportLineItemTypes.DivisionTotal)[0] || undefined,
      ),
    ];
  }, [sortedTables, report]);
  const totalUnassignedCost = React.useMemo(
    () => totalUnassignedCostsRow.reduce((sum, item) => sum + (item.totalBudget || 0), 0),
    [totalUnassignedCostsRow],
  );

  const totalBudget = React.useMemo(() => {
    return report.lineItems.filter(li => !li.isDeleted).reduce((acc, row) => acc + row.totalBudget, 0);
  }, [report.lineItems]);

  const hardCostTotal = React.useMemo(() => {
    return projectedHardCostSubTotalRow.reduce((acc, item) => acc + item.totalBudget, 0);
  }, [projectedHardCostSubTotalRow]);

  const cdccHardCostTotal = React.useMemo(() => {
    return projectedHardCostSubTotalRow.reduce((acc, item) => acc + calculateCdccCompTotal(item), 0);
  }, [projectedHardCostSubTotalRow]);

  const cdccCostPerGSF = React.useMemo(() => {
    if (!report.generalInfo?.grossBuildingSF || report.generalInfo.grossBuildingSF == 0) return 0;
    return cdccHardCostTotal / report.generalInfo?.grossBuildingSF;
  }, [report.generalInfo, cdccHardCostTotal]);

  const deviationPercentage = React.useMemo(() => {
    if (cdccHardCostTotal == 0) return 0;
    return totalBudget / cdccHardCostTotal - 1;
  }, [totalBudget, cdccHardCostTotal]);

  const saveReportWithCalculations = React.useCallback(() => {
    saveReport({ contractorTotal: hardCostTotal, cdccTotal: cdccHardCostTotal, cdccCostPerSF: cdccCostPerGSF, deviationPercentage });
  }, [hardCostTotal, cdccHardCostTotal, cdccCostPerGSF, deviationPercentage]);

  const costPerUnit: CostPerUnit = React.useMemo(() => {
    const grossBuildingSF = report?.generalInfo?.grossBuildingSF ?? 0;
    const netRentableSF = report?.generalInfo?.netRentableSF ?? 0;
    const unitCount = report?.generalInfo?.unitCount ?? 0;

    return {
      hardCostPerBuildingGSF: grossBuildingSF > 0 ? hardCostTotal / grossBuildingSF : 0,
      projectCostPerBuildingNRSF: netRentableSF > 0 ? totalBudget / netRentableSF : 0,
      projectCostPerUnit: unitCount > 0 ? totalBudget / unitCount : 0,
    };
  }, [report, totalBudget, hardCostTotal]);

  const costComparison: CostComparison = React.useMemo(() => {
    return {
      contractorHardCostTotal: hardCostTotal,
      cdccHardCostTotal: cdccHardCostTotal,
      cdccCostPerGSF: cdccCostPerGSF,
      deviationPercentage: deviationPercentage,
    };
  }, [hardCostTotal, cdccHardCostTotal, cdccCostPerGSF, deviationPercentage]);

  const renderActionsCell = React.useCallback(
    (params: any) => (
      <Tooltip title='Edit line item' arrow={false}>
        <WhiteFab
          aria-label='edit'
          icon={<Edit fontSize='small' />}
          textColor='primary'
          onClick={(e: any) => {
            e.stopPropagation();
            setSelectedLineItem(params.row);
            setLineItemEditDialogOpen(true);
          }}
          size='small'
          disabled={isLocked}
        />
      </Tooltip>
    ),
    [isLocked],
  );

  // Convert DataGrid columns to MRT columns
  const convertToMRTColumns = React.useCallback(
    (gridColumns: any[]) => {
      return gridColumns.map(col => {
        const mrtColumn: MRT_ColumnDef<any> = {
          accessorKey: col.valueGetter ? undefined : col.field,
          accessorFn: col.valueGetter ? (row: any) => col.valueGetter(null, row) : undefined,
          header: col.headerName || col.field,
          size: col.width || (col.flex ? 250 : col.minWidth || 120),
          minSize: col.minWidth || 80,
          maxSize: col.maxWidth || 600,
          grow: Boolean(col.flex),
          enableSorting: col.sortable !== false,
          enableColumnFilter: false,
          enableEditing: !isLocked && col.editable,
          muiTableHeadCellProps: {
            className: col.headerClassName,
            sx: {
              textAlign: col.type === 'number' ? 'right' : 'left',
              margin: 0,
            },
          },
          muiTableBodyCellProps: ({ row }: { row: any }) => ({
            className: col.cellClassName,
            sx: {
              textAlign: col.type === 'number' ? 'right' : 'left',
              borderLeft: 'none',
              borderRight: 'none',
              height: row.original?.note?.content ? 'auto' : '52px',
              minHeight: '52px',
              padding: '8px 16px',
              margin: 0,
              // Allow text wrapping for notes
              whiteSpace: row.original?.note?.content ? 'normal' : 'nowrap',
              overflow: 'visible',
            },
          }),
        };

        // Adapt DataGrid renderCell to MRT Cell
        if (col.renderCell) {
          mrtColumn.Cell = ({ cell, row }) => {
            const params = {
              value: cell.getValue(),
              row: row.original,
              field: col.field,
              id: row.id,
              api: null, // Not available in MRT
              hasFocus: false,
              tabIndex: -1,
            };
            return col.renderCell(params);
          };
        } else if (col.valueFormatter) {
          // Handle valueFormatter when no custom renderCell
          mrtColumn.Cell = ({ cell, row }) => {
            const value = cell.getValue();
            const formattedValue = col.valueFormatter(value, row.original, col, null);
            return <span>{formattedValue}</span>;
          };
        }

        // Adapt DataGrid renderEditCell to MRT Edit
        if (col.renderEditCell) {
          mrtColumn.Edit = ({ cell, row, table }) => {
            // Don't show edit cells for totals rows
            if (row.original.id === TOTAL_ID || row.original.id?.toString().includes('total')) {
              return null;
            }

            const params = {
              value: cell.getValue(),
              row: row.original,
              field: col.field,
              id: row.id,
              api: {
                setEditCellValue: ({ id, field, value }: any) => {
                  // MRT handles this differently - the value will be handled by onEditingRowSave
                },
                stopCellEditMode: ({ id, field, ignoreModifications }: any) => {
                  if (!ignoreModifications) {
                    table.setEditingRow(null);
                  }
                },
              },
              hasFocus: true,
              tabIndex: 0,
              error: null, // You might need to implement validation errors
            };
            return col.renderEditCell(params);
          };
        }

        return mrtColumn;
      });
    },
    [isLocked],
  );

  const columns = React.useMemo(() => {
    const gridColumns = getReportColumns(null, renderActionsCell).map(col => {
      if (isLocked) {
        col.editable = false;
      }
      if (col.field === 'description') {
        return {
          ...col,
          minWidth: 480,
        };
      }
      return col;
    });
    return convertToMRTColumns(gridColumns);
  }, [isLocked, renderActionsCell, convertToMRTColumns]);

  const updateLineItem = React.useCallback((updatedLineItem: ReportLineItem) => {
    updateReport(report => {
      const updatedLineItems = report.lineItems.map(existingLI => {
        return existingLI.id === updatedLineItem.id ? { ...updatedLineItem, isEdited: true } : existingLI;
      });
      return { ...report, lineItems: updatedLineItems };
    });
  }, []);

  const updateReportWithModifiedLineItems = React.useCallback((modifiedLineItems: ReportLineItem[]) => {
    updateReport(report => {
      const updatedLineItems = report.lineItems.map(existingLI => {
        const modifiedLineItem = modifiedLineItems.find(modifiedLI => modifiedLI.id === existingLI.id);
        return modifiedLineItem || existingLI;
      });
      return { ...report, lineItems: updatedLineItems };
    });
  }, []);

  const saveLineItem = React.useCallback(
    async (lineItemData: CreateLineItemDTO, showNotification = true) => {
      try {
        const reportId = report.id;
        lineItemData.reportId = reportId;
        if (isReportDirtyRef.current) {
          const isConfirmed = await getConfirmation({
            description: 'Creating a new LineItem will overwrite any unsaved changes in the Report. Are you sure you want to continue?',
            confirmButton: {
              label: 'Continue',
              color: 'error',
            },
            cancelButton: {
              label: 'Cancel',
              color: 'secondary',
            },
          });
          if (!isConfirmed) return;
        }
        await createLineItem(reportId, lineItemData);
        if (showNotification) {
          toast.success('Line Item added successfully!');
        }
        refetchReport();
      } catch (error) {
        handleError(error);
      }
    },
    [getConfirmation, refetchReport, report.id],
  );

  const updateGeneralInfo = React.useCallback(
    (updatedGeneralInfo: ReportGeneralInfo) => {
      updateReport(report => ({ ...report, generalInfo: { ...updatedGeneralInfo, isEdited: true } }));
    },
    [report],
  );

  const updateDynamicData = React.useCallback(
    (updatedDynamicDataString: string) => {
      updateReport(report => ({ ...report, dynamicData: updatedDynamicDataString }));
    },
    [report],
  );

  const updateDivisionDraws = React.useCallback(
    async (divisionId: string, newDrawsIds: string[]) => {
      await updateDivisionProjectDraws(report.id, {
        divisionId: divisionId,
        newProjectDrawIds: newDrawsIds,
      });
      queryClient.invalidateQueries([keys.caReport, report.id]);
      queryClient.invalidateQueries([keys.compsAverages]);
      queryClient.invalidateQueries(keys.divisions);
    },
    [report.id],
  );

  const handleSavePdf = React.useCallback(async () => {
    const currentDateString = new Date().toLocaleDateString();
    try {
      const content = createCostAnalysisReportPdf({
        report,
        costPerUnit,
        costComparison,
        sortedDivisions,
        sortedTables,
        hardCostSubTotalRow,
        projectedHardCostSubTotalRow,
        softCostSubTotalRow,
        totalProjectCostsRow,
        revisedBudget: totalBudget,
        notes,
      });

      const documentDefinition: TDocumentDefinitions = {
        content,
        pageSize: 'A3' as const,
        pageOrientation: 'landscape' as const,
        header: {
          text: `Exported on ${currentDateString}`,
          alignment: 'right',
          margin: [50, 10, 50, 0],
        },
        pageMargins: [50, 10, 50, 0],
      };
      const pdfMake = await import('pdfmake/build/pdfmake.min');
      window.pdfMake = pdfMake;
      setupFontsForPdfMake();
      pdfMake
        .createPdf(documentDefinition)
        .download(`${report.generalInfo.reportName || 'Cost_Analysis_Report'}_${report.status ?? ''}_${currentDateString}.pdf`);
      toast.success('PDF is generated successfully. Please check your downloads folder.');
    } catch (error) {
      handleError(error);
    }
  }, [
    report,
    costPerUnit,
    costComparison,
    sortedDivisions,
    sortedTables,
    hardCostSubTotalRow,
    projectedHardCostSubTotalRow,
    softCostSubTotalRow,
    totalProjectCostsRow,
    totalBudget,
    notes,
  ]);

  const handleCreateCopy = React.useCallback(() => {
    setOpenCopyReportDialog(true);
  }, []);

  const handleExportInvoice = React.useCallback(() => {
    window.open(`${ROUTES.CA_REPORTS}/${report.id}/invoice`, '_blank');
  }, [report.id]);

  const toggleLock = React.useCallback(async () => {
    if (isLocked && report.status == ReportStatusEnum.Completed) {
      toast.error('Report is marked as completed and cannot be unlocked. Please make a copy to edit.');
      return;
    }
    if (!isLocked) {
      const isConfirmed = await getConfirmation({
        description: 'Locking the report will discard any unsaved changes. Are you sure you want to continue?',
        confirmButton: {
          label: 'Continue',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;

      if (isReportDirty) {
        undoChanges();
      }
    }

    setIsLocked(!isLocked);
  }, [isLocked, isReportDirty, report.status]);

  const handleCreateCopyCA = React.useCallback(
    async (reportName: string) => {
      try {
        const response = await copyReport(report.id, reportName);
        toast.success('Report copied successfully!');
        navigate(`${ROUTES.CA_REPORTS}/${response.id}`, { state: { fromReportsList: true, reportId: response.id } });
      } catch (error: any) {
        toast.error(error.message || error.response?.data?.message || 'Error copying report.');
      }
    },
    [report.id],
  );

  useEffect(() => {
    if (shouldRunPricing) {
      handleGlobalAddComps();
      setShouldRunPricing(false);
    }
  }, [report, shouldRunPricing]);

  const addCompsWithDivisions = React.useCallback(
    async (selectedCompIds: string[], selectedDivisionIds: string[]) => {
      if (selectedCompIds.length === 0) {
        toast.error('Please select at least one project to add.');
        return;
      }

      if (selectedDivisionIds.length === 0) {
        toast.error('Please select at least one division.');
        return;
      }

      setIsAddingComps(true);
      setCompLoading(true);
      try {
        if (isReportDirty) {
          const isConfirmed = await getConfirmation({
            description: 'Adding these comps will overwrite any unsaved changes in the Report. Are you sure you want to continue?',
            confirmButton: {
              label: 'Continue',
              color: 'error',
            },
            cancelButton: {
              label: 'Cancel',
              color: 'secondary',
            },
          });
          if (!isConfirmed) return;
        }

        // Track newly selected comps

        // Extract project IDs from report.selectedComps
        const reportProjectIds = report.selectedComps.map(comp => comp.project.projectId);

        // Combine both lists of project IDs (avoid duplicates if necessary)
        const combinedProjectIds = Array.from(new Set([...selectedCompIds, ...reportProjectIds]));

        // Send the API call with both project IDs and division IDs
        await generateCompProjectAnalysis({
          caReportId: report.id,
          projectIds: combinedProjectIds,
          divisionIds: selectedDivisionIds, // Pass the selected division IDs
        });
        await queryClient.invalidateQueries([keys.compsAverages]);

        refetchReport();
        toast.success('Comps Added successfully!');
        // Don't open division selection dialog here since user already selected divisions
        setIsAddCompsWithDivisionsDialogOpen(false);
      } catch (error) {
        handleError(error);
        handleCloseDivisionSelectionDialog();
      } finally {
        setIsAddingComps(false);
        setCompLoading(false);
      }
    },
    [report.id, report.selectedComps, isReportDirty, handleCloseDivisionSelectionDialog],
  );

  return (
    <>
      <ReportRenderer
        divisionTables={divisionTables}
        sortedDivisions={sortedDivisions}
        sortedTables={sortedTables}
        report={report}
        columns={columns}
        isLocked={isLocked}
        predictingDivisions={predictingDivisions}
        softCostSubTotalRow={softCostSubTotalRow}
        hardCostSubTotalRow={hardCostSubTotalRow}
        projectedHardCostSubTotalRow={projectedHardCostSubTotalRow}
        totalProjectCostsRow={totalProjectCostsRow}
        costPerUnit={costPerUnit}
        costComparison={costComparison}
        isReportLoading={isReportLoading}
        compLoading={compLoading}
        isDeletingComp={isDeletingComp}
        deletingCompId={deletingCompId}
        totalBudget={totalBudget}
        notes={notes}
        isReportAuthorSameAsLoggedInUser={isReportAuthorSameAsLoggedInUser}
        isDeletingReport={isDeletingReport}
        isAddingComps={isAddingComps}
        isSaving={isSaving}
        toggleLock={toggleLock}
        onDeleteReport={onDeleteReport}
        onDeleteComp={onDeleteComp}
        handleSavePdf={handleSavePdf}
        handleCreateCopy={handleCreateCopy}
        handleGlobalAddComps={handleGlobalAddComps}
        saveReportWithCalculations={saveReportWithCalculations}
        handleOpenCompsDialog={handleOpenCompsDialog}
        setNotes={setNotes}
        saveLineItem={saveLineItem}
        openAutoAssignDialog={openAutoAssignDialog}
        updateReport={updateReport}
        updateLineItem={updateLineItem}
        setSelectedLineItem={setSelectedLineItem}
        setLineItemEditDialogOpen={setLineItemEditDialogOpen}
        setLineItemNoteDialogOpen={setLineItemNoteDialogOpen}
        handleOpenProjectDialog={handleOpenProjectDialog}
        registerReturnComp={registerReturnComp}
        updateGeneralInfo={updateGeneralInfo}
        updateDynamicData={updateDynamicData}
        updateReportWithModifiedLineItems={updateReportWithModifiedLineItems}
        totalUnassignedCost={totalUnassignedCost}
        isReportDirty={isReportDirty}
        selectedComparableIds={selectedComparableIds}
        setSelectedComparableIds={setSelectedComparableIds}
        onSelectDivisionsForComps={handleOpenDivisionSelectionForComps} // to be implemented
        onExportInvoice={handleExportInvoice}
      />
      <LineItemEditDialog
        open={lineItemEditDialogOpen}
        onClose={() => {
          setLineItemEditDialogOpen(false);
          // Delay to allow the dialog to close before resetting the selected line item
          setTimeout(() => {
            setSelectedLineItem(null);
          }, 1000);
        }}
        lineItem={selectedLineItem}
        updateLineItem={updateLineItem}
        divisions={divisions}
      />
      <LineItemNoteDialog
        open={lineItemNoteDialogOpen}
        onClose={handleCloseDialog}
        note={selectedLineItem?.note}
        lineItem={selectedLineItem?.lineItem.description}
        onSave={handleupdatenote}
        isLoading={isSavingNote}
      />
      <AutoAssignDivisionsDialog
        open={isAutoAssignDialogOpen}
        onClose={() => setIsAutoAssignDialogOpen(false)}
        lineItems={lineItemsToAutoAssign}
        updateDivisions={updateDivisions}
        divisions={divisions}
        useTotalBudget={true}
        showToggle={false}
      />
      <ProjectDrawSelectionDialog
        updateDivisionDraws={updateDivisionDraws}
        divisionId={openDialogDivisionId || ''}
        divisionCode={openDialogDivisionCode}
        divisionName={openDialogDivisionName}
        allProjectDraws={report.selectedDraws}
        selectedProjectDraws={selectedProjectDraws}
        onSelectionChange={handleProjectDrawSelectionChange}
        open={Boolean(openDialogDivisionId)}
        onClose={handleCloseDialog}
        isReportDirty={isReportDirty}
      />
      <GenericCopyReportDialog
        open={openCopyReportDialog}
        onClose={handleCloseDialog}
        defaultReportName={`${report.generalInfo.reportName} (Copy)`}
        handleCreateCopy={handleCreateCopyCA}
      />
      <AddCompsWithDivisionsDialog
        open={isAddCompsWithDivisionsDialogOpen}
        onClose={() => setIsAddCompsWithDivisionsDialogOpen(false)}
        divisions={divisions}
        onSelectCompsAndDivisions={addCompsWithDivisions} // Use the new function
        isAdding={isAddingComps}
        disabledCompsIds={[...report.selectedComps.map(comp => comp.project.projectId), report?.project?.projectId]}
      />
      <DivisionSelectionDialog
        open={isDivisionSelectionDialogOpen}
        onClose={handleCloseDivisionSelectionDialog}
        divisions={divisions}
        selectedComparables={selectedComparables}
        onApply={handleEditDivisionsForComps}
        isApplying={isApplyingToDivisions}
      />
    </>
  );
};
